import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children, userId }) => {
  const [modules, setModules] = useState([]);
  const [moduleData, setModuleData] = useState({});
  const [groupData, setGroupData] = useState({});
  const [wordsData, setWordsData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');

  // Fetch modules for dashboard (minimal data for navigation)
  const fetchModulesForDashboard = useCallback(async () => {
    if (!userId) {
      setModules([]);
      return;
    }

    setLoading(prev => ({ ...prev, modules: true }));
    setError('');

    try {
      // Only grab what's needed for dashboard display and navigation
      // Include classroom_id to filter private modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules_new')
        .select('id, position, from_lang, to_lang, num_groups, num_words, created_at, name, classroom_id')
        .eq('user_id', userId)
        .order('position', { ascending: false });

      if (modulesError) {
        setError('Error fetching modules');
        return;
      }

      setModules(modulesData || []);
    } catch (err) {
      setError('Error fetching modules');
    } finally {
      setLoading(prev => ({ ...prev, modules: false }));
    }
  }, [userId]);

  // Fetch specific module data (using ID when available for efficiency)
  const fetchModuleData = useCallback(async (modulePosition) => {
    if (!userId || !modulePosition) {
      return null;
    }

    // Check if we already have this module in cache
    if (moduleData[modulePosition]) {
      return moduleData[modulePosition];
    }

    // Check if this is a module ID (UUID) or position (number)
    const isUUID = typeof modulePosition === 'string' && modulePosition.includes('-');
    
    // Try to find the module from the existing modules array first
    const existingModule = isUUID 
      ? modules.find(m => m.id === modulePosition)
      : modules.find(m => m.position === Number(modulePosition));
    if (existingModule) {
      // If we have the module in the array, use it
      setModuleData(prev => ({ ...prev, [modulePosition]: existingModule }));
      return existingModule;
    }

    // Only query Supabase if the module is not in the modules array
    setLoading(prev => ({ ...prev, [`module_${modulePosition}`]: true }));

    try {
      // Check if this is a module ID (UUID) or position (number)
      const isUUID = typeof modulePosition === 'string' && modulePosition.includes('-');
      
      let query = supabase
        .from('modules_new')
        .select('*');
      
      if (isUUID) {
        // It's a module ID (classroom module) - don't filter by user_id
        query = query.eq('id', modulePosition);
      } else {
        // It's a position (private module) - filter by user_id
        query = query.eq('position', Number(modulePosition))
                     .eq('user_id', userId);
      }
      
      const { data, error } = await query.single();

      if (error) {
        setError('Error fetching module');
        return null;
      }

      setModuleData(prev => ({ ...prev, [modulePosition]: data }));
      return data;
    } catch (err) {
      setError('Error fetching module');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [`module_${modulePosition}`]: false }));
    }
  }, [userId, moduleData, modules]);

  // Fetch specific word group data
  const fetchGroupData = useCallback(async (modulePosition, groupPosition) => {
    if (!userId || !modulePosition || !groupPosition) {
      return null;
    }

    const cacheKey = `${modulePosition}_${groupPosition}`;
    if (groupData[cacheKey]) {
      return groupData[cacheKey];
    }

    setLoading(prev => ({ ...prev, [`group_${cacheKey}`]: true }));

    try {
      // First, get the module to get its actual ID
      const module = await fetchModuleData(modulePosition);
      if (!module) {
        setError('Module not found');
        return null;
      }

      // Try to find the word group by position first (since we're using positions in URLs)
      let { data, error } = await supabase
        .from('word_groups')
        .select('*')
        .eq('module_id', module.id)
        .eq('position', Number(groupPosition))
        .single();

      // If position lookup fails, try to find by ID (for backward compatibility)
      if (error) {

        const { data: idData, error: idError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', module.id)
          .eq('id', groupPosition)
          .single();

        if (idError) {
          setError('Error fetching word group');
          return null;
        }
        data = idData;
      }

      setGroupData(prev => ({ ...prev, [cacheKey]: data }));
      return data;
    } catch (err) {
      setError('Error fetching word group');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [`group_${cacheKey}`]: false }));
    }
  }, [userId, groupData, fetchModuleData]);

  // Fetch words for a specific group (with SRS data from word_srs_data)
  const fetchWordsData = useCallback(async (groupId) => {
    if (!userId || !groupId || wordsData[groupId]) {
      return wordsData[groupId] || [];
    }

    setLoading(prev => ({ ...prev, [`words_${groupId}`]: true }));

    try {
      const { data, error } = await supabase
        .from('words')
        .select(`
          id,
          group_id,
          from_text,
          to_text,
          position,
          created_at,
          updated_at,
          word_srs_data!left (
            sm2_interval,
            learning_interval,
            sm2_ease,
            learning_ease,
            next_due,
            apparent_tier,
            history,
            updated_at
          )
        `)
        .eq('group_id', groupId)
        .eq('word_srs_data.user_id', userId)
        .order('position', { ascending: true });

      if (error) {
        setError('Error fetching words');
        return [];
      }

      // Merge SRS data into words
      const mergedWords = (data || []).map(word => {
        const srs = word.word_srs_data?.[0] || null;
        return {
          ...word,
          word_srs_data: undefined,
          sm2_interval: srs?.sm2_interval ?? 0,
          learning_interval: srs?.learning_interval ?? 0,
          ease: srs?.sm2_ease ?? 2.5,
          next_due: srs?.next_due ?? 0,
          apparent_tier: srs?.apparent_tier ?? 1,
          history: srs?.history 
            ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
            : [],
          srs_updated_at: srs?.updated_at || word.updated_at
        };
      });

      setWordsData(prev => ({ ...prev, [groupId]: mergedWords }));
      return mergedWords;
    } catch (err) {
      setError('Error fetching words');
      return [];
    } finally {
      setLoading(prev => ({ ...prev, [`words_${groupId}`]: false }));
    }
  }, [userId, wordsData]);

  // Clear cache for specific data
  const clearCache = useCallback((type, key) => {
    switch (type) {
      case 'module':
        setModuleData(prev => {
          const newData = { ...prev };
          delete newData[key];
          return newData;
        });
        break;
      case 'group':
        setGroupData(prev => {
          const newData = { ...prev };
          delete newData[key];
          return newData;
        });
        break;
      case 'words':
        setWordsData(prev => {
          const newData = { ...prev };
          delete newData[key];
          return newData;
        });
        break;
      default:
        break;
    }
  }, []);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    setModuleData({});
    setGroupData({});
    setWordsData({});
  }, []);

  // Refresh modules (useful after create/delete operations)
  const refreshModules = useCallback(() => {
    setModules([]); // Clear current modules to trigger refetch
    fetchModulesForDashboard();
  }, [fetchModulesForDashboard]);

  // Initialize dashboard data
  useEffect(() => {
    if (userId) {
      fetchModulesForDashboard();
    }
  }, [userId, fetchModulesForDashboard]);

  const value = {
    modules,
    moduleData,
    groupData,
    wordsData,
    loading,
    error,
    fetchModulesForDashboard,
    fetchModuleData,
    fetchGroupData,
    fetchWordsData,
    clearCache,
    clearAllCache,
    refreshModules,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
